import React, { useState, useEffect } from 'react';
import { Music, Gamepad2, Users, Mic2, Radio, Clock, Instagram, MessageCircle, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { defaultContent } from '../data/mock';
import { fetchContent } from '../lib/content';
import { toast } from '../hooks/use-toast';

const Home = () => {
  // Start from the bundled defaults so the page renders instantly, then swap in
  // the admin-edited content once it loads from the API.
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    let active = true;
    fetchContent().then((c) => {
      if (active) setContent(c);
    });
    return () => {
      active = false;
    };
  }, []);

  const { brand, images, hero, services, about, gaming, schedule, contact, footer } = content;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Create WhatsApp message
    const whatsappMessage = `Hi, I'm ${formData.name}
Service: ${formData.service}
Email: ${formData.email}
Phone: ${formData.phone}

Message: ${formData.message}`;

    const whatsappUrl = `https://wa.me/${contact.whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');

    toast({
      title: "Redirecting to WhatsApp!",
      description: "Your message is ready to send via WhatsApp.",
    });

    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={images.logo} alt="Windrise Studios" className="h-10 w-10" />
            <span className="text-xl font-bold tracking-tight">{brand.name}</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-sm hover:text-amber-500 transition-colors">Services</a>
            <a href="#about" className="text-sm hover:text-amber-500 transition-colors">About</a>
            <a href="#gaming" className="text-sm hover:text-amber-500 transition-colors">Gaming</a>
            <a href="#schedule" className="text-sm hover:text-amber-500 transition-colors">Schedule</a>
            <a href="#contact" className="text-sm hover:text-amber-500 transition-colors">Contact</a>
          </div>
          <div className="flex items-center gap-3">
            <a href={`https://wa.me/${contact.whatsappNumber}`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                <MessageCircle className="h-4 w-4 mr-2" />
                Book Now
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={images.studio1}
            alt="Windrise Studio"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="mb-6 inline-block">
            <Badge className="bg-amber-600/20 text-amber-500 border border-amber-600/30 px-4 py-1.5 text-sm">
              <Radio className="h-3 w-3 mr-2 inline" />
              {hero.badge}
            </Badge>
          </div>

          <h1 className="text-6xl md:text-8xl font-black mb-6 tracking-tighter">
            {hero.titleLine1}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-600">
              {hero.titleLine2}
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            {hero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="#services">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg">
                <Music className="h-5 w-5 mr-2" />
                View Services
              </Button>
            </a>
            <a href={contact.instagramUrl} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-zinc-700 hover:bg-zinc-900 px-8 py-6 text-lg">
                <Instagram className="h-5 w-5 mr-2" />
                Follow Us
              </Button>
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-zinc-700 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-amber-500 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 px-6 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-amber-600/20 text-amber-500 border border-amber-600/30 mb-4">
              Our Services
            </Badge>
            <h2 className="text-5xl md:text-6xl font-black mb-6 tracking-tight text-white">{services.heading}</h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              {services.subheading}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.items.map((service) => (
              <Card
                key={service.id}
                className="bg-zinc-900 border-zinc-800 hover:border-amber-600/50 transition-all duration-300 group cursor-pointer"
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="p-3 bg-amber-600/10 rounded-lg group-hover:bg-amber-600/20 transition-colors">
                      {service.category === 'gaming' ? (
                        <Gamepad2 className="h-6 w-6 text-amber-500" />
                      ) : (
                        <Mic2 className="h-6 w-6 text-amber-500" />
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-amber-500">{service.price}</div>
                      <div className="text-xs text-zinc-500">{service.duration}</div>
                    </div>
                  </div>
                  <CardTitle className="text-xl text-white">{service.title}</CardTitle>
                  <CardDescription className="text-zinc-400">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-zinc-300 flex items-center">
                        <div className="w-1 h-1 bg-amber-500 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Studio Images */}
      <section id="about" className="py-32 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-amber-600/20 text-amber-500 border border-amber-600/30 mb-4">
                About Us
              </Badge>
              <h2 className="text-5xl md:text-6xl font-black mb-6 tracking-tight leading-tight text-white">
                {about.heading}
              </h2>
              {about.paragraphs.map((para, idx) => (
                <p key={idx} className="text-lg text-zinc-400 mb-6 leading-relaxed">
                  {para}
                </p>
              ))}
              <div className="grid grid-cols-2 gap-6 mt-2">
                {about.stats.map((stat, idx) => (
                  <div key={idx} className="p-6 bg-zinc-900 rounded-lg border border-zinc-800">
                    {idx === 0 ? (
                      <Music className="h-8 w-8 text-amber-500 mb-3" />
                    ) : (
                      <Users className="h-8 w-8 text-amber-500 mb-3" />
                    )}
                    <div className="text-3xl font-bold mb-1">{stat.value}</div>
                    <div className="text-sm text-zinc-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src={images.studio2}
                  alt="Studio Recording Setup"
                  className="w-full h-64 object-cover rounded-lg border border-zinc-800"
                />
                <img
                  src={images.studio4}
                  alt="Studio Equipment"
                  className="w-full h-48 object-cover rounded-lg border border-zinc-800"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src={images.studio3}
                  alt="Mixing Console"
                  className="w-full h-48 object-cover rounded-lg border border-zinc-800"
                />
                <img
                  src={images.studio5}
                  alt="Studio Atmosphere"
                  className="w-full h-64 object-cover rounded-lg border border-zinc-800"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gaming Lounge Section */}
      <section id="gaming" className="py-32 px-6 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-amber-600/20 text-amber-500 border border-amber-600/30 mb-4">
              Gaming Lounge
            </Badge>
            <h2 className="text-5xl md:text-6xl font-black mb-6 tracking-tight text-white">{gaming.heading}</h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              {gaming.subheading}
            </p>
          </div>

          <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2">
                <div className="p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <Gamepad2 className="h-10 w-10 text-amber-500" />
                    <div>
                      <h3 className="text-2xl font-bold text-white">{gaming.setupTitle}</h3>
                      <p className="text-sm text-zinc-400">{gaming.setupSubtitle}</p>
                    </div>
                  </div>

                  <p className="text-zinc-300 mb-8 leading-relaxed">
                    {gaming.description}
                  </p>

                  <h4 className="text-lg font-semibold mb-4 text-white">Available Games:</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {gaming.games.map((game) => (
                      <div
                        key={game.id}
                        className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg border border-zinc-700"
                      >
                        <span className="font-medium text-white">{game.name}</span>
                        <Badge variant="outline" className="border-amber-600/30 text-amber-500">
                          {game.genre}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative h-full min-h-[400px]">
                  <img
                    src={images.studio6}
                    alt="Gaming Lounge"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 to-transparent"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Schedule/Calendar Section */}
      <section id="schedule" className="py-32 px-6 bg-zinc-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-amber-600/20 text-amber-500 border border-amber-600/30 mb-4">
              Studio Schedule
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight text-white">
              {schedule.heading}
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              {schedule.subheading}
            </p>
          </div>

          <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
            <CardContent className="p-0">
              <div className="relative w-full" style={{ paddingBottom: '75%', minHeight: '600px' }}>
                <iframe
                  src={schedule.calendarSrc}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                  frameBorder="0"
                  scrolling="no"
                  title="Windrise Studios Calendar"
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <Badge className="bg-amber-600/20 text-amber-500 border border-amber-600/30 mb-4">
                Get In Touch
              </Badge>
              <h2 className="text-5xl md:text-6xl font-black mb-6 tracking-tight leading-tight text-white">
                {contact.heading}
              </h2>
              <p className="text-lg text-zinc-400 mb-8 leading-relaxed">
                {contact.paragraph}
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-600/10 rounded-lg">
                    <Clock className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Studio Hours</h4>
                    <p className="text-sm text-zinc-400">{contact.hours}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-600/10 rounded-lg">
                    <MessageCircle className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">WhatsApp</h4>
                    <a href={`https://wa.me/${contact.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="text-sm text-amber-500 hover:text-amber-400">
                      {contact.whatsappDisplay}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-600/10 rounded-lg">
                    <Instagram className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Instagram</h4>
                    <a href={contact.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-amber-500 hover:text-amber-400">
                      {contact.instagramHandle}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-amber-600/10 rounded-lg">
                    <MapPin className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1 text-white">Location</h4>
                    <a
                      href={contact.locationMapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-zinc-400 hover:text-amber-500 transition-colors"
                    >
                      {contact.locationAddress}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Send us a message</CardTitle>
                <CardDescription>We'll respond via WhatsApp or Instagram DM</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-zinc-800 border-zinc-700 focus:border-amber-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-zinc-800 border-zinc-700 focus:border-amber-500"
                    />
                    <Input
                      name="phone"
                      type="tel"
                      placeholder="Phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-zinc-800 border-zinc-700 focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-white focus:border-amber-500 focus:outline-none"
                    >
                      <option value="">Select Service</option>
                      {services.items.map((service) => (
                        <option key={service.id} value={service.title}>
                          {service.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Textarea
                      name="message"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="bg-zinc-800 border-zinc-700 focus:border-amber-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6"
                  >
                    <Mail className="h-5 w-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-zinc-950 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={images.logo} alt="Windrise Studios" className="h-8 w-8" />
              <span className="font-bold">{brand.name}</span>
            </div>

            <div className="flex items-center gap-6">
              <a href={contact.instagramUrl} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-amber-500 transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
              <a href={`https://wa.me/${contact.whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-amber-500 transition-colors">
                <MessageCircle className="h-5 w-5" />
              </a>
            </div>

            <p className="text-sm text-zinc-500">
              {footer.copyright}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
